import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet.js';
import { ApiError } from '@/lib/api/client.js';
import { testBundleExpansion } from './api.js';
import { BundleExpansionResult } from './bundle-expansion-result.js';

/**
 * « Tester un SKU » depuis la liste (`02` §8) — un **tiroir**, plus une
 * pop-up. Sans sélecteur de boutique : un bundle a une seule composition
 * (décision du 10/09/2026).
 */
const testFormSchema = z.object({
  sku: z.string().min(1, 'SKU requis'),
  qty: z.coerce.number().int().positive().default(1),
});

type TestFormValues = z.infer<typeof testFormSchema>;

export interface BundleTestSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BundleTestSheet({ open, onOpenChange }: BundleTestSheetProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TestFormValues>({
    resolver: zodResolver(testFormSchema) as Resolver<TestFormValues>,
    defaultValues: { sku: '', qty: 1 },
  });

  const mutation = useMutation({
    mutationFn: (values: TestFormValues) =>
      testBundleExpansion({ sku: values.sku, qty: values.qty }),
  });

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
        }
        onOpenChange(next);
      }}
    >
      <SheetContent className="sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Tester un SKU</SheetTitle>
          <SheetDescription>
            Montre les lignes qui partiraient chez Martelet pour ce SKU et cette quantité.
          </SheetDescription>
        </SheetHeader>

        {/* Enveloppe : un `<form>` enfant direct du tiroir s'étire sur toute la hauteur. */}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <form
              className="flex flex-wrap items-end gap-2"
              noValidate
              onSubmit={(event) => {
                void handleSubmit((values) => {
                  mutation.mutate(values);
                })(event);
              }}
            >
              <Field label="SKU" htmlFor="test-sku" className="min-w-40 flex-1">
                <Input
                  id="test-sku"
                  aria-invalid={errors.sku ? true : undefined}
                  {...register('sku')}
                />
              </Field>
              <Field label="Quantité" htmlFor="test-qty">
                <Input id="test-qty" type="number" min={1} className="w-24" {...register('qty')} />
              </Field>
              <Button type="submit" isLoading={isSubmitting || mutation.isPending}>
                Tester
              </Button>
            </form>
            {errors.sku ? <p className="text-xs text-status-red-fg">{errors.sku.message}</p> : null}
          </div>

          {mutation.isError ? (
            <ErrorState
              message={
                mutation.error instanceof ApiError
                  ? mutation.error.message
                  : 'Une erreur technique est survenue.'
              }
              detail={mutation.error instanceof ApiError ? mutation.error.detail : undefined}
            />
          ) : null}

          {mutation.data ? <BundleExpansionResult result={mutation.data} /> : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
